/**
 * src/hooks/useUserOrgCountry.ts — fetch the logged-in admin's org country code.
 *
 * v0.4.9.2.3c22a: shared hook for the three admin pages
 * (Most Wanted, Missing Persons, Stolen Vehicles). Used to pre-fill the
 * `country` field on the Add form so admins don't have to retype their
 * org's alpha-3 country code every time.
 *
 * The hook returns the alpha-3 country code (ISO-3166-1) suitable for
 * passing straight into the new SearchableSelect for the country field.
 *
 * Behavior:
 *   - On mount, if `user.civil_respondent_id` is set, fetch the org via
 *     `api.getCivilRespondent()`. One extra API call per page load.
 *   - Prefer the org's `country_code` column (alpha-3, normalized).
 *   - Fall back to `country` if it already looks alpha-3.
 *   - Returns '' if the user has no org, the fetch fails, or the org has
 *     no usable country.
 *
 * NOTE: SuperAdmin users typically have `civil_respondent_id = null`, so
 * no pre-fill happens. They have to pick a country manually — that matches
 * their role (cross-org, no specific country).
 */
import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Returns the alpha-3 country code of the logged-in admin's civil-
 * respondent org, or '' if unknown / not applicable.
 */
export function useUserOrgCountry(): string {
  const { user } = useAuth();
  const [code, setCode] = useState<string>('');

  useEffect(() => {
    const orgId = user?.civil_respondent_id;
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const org: any = await api.getCivilRespondent(orgId);
        if (cancelled) return;
        const a3 = org?.country_code;
        const legacy = org?.country;
        const finalCode = (typeof a3 === 'string' && a3.length === 3)
          ? a3
          : (typeof legacy === 'string' && /^[A-Z]{3}$/.test(legacy) ? legacy : '');
        setCode(finalCode);
      } catch (_) {
        // Silent: the user can pick the country manually if the fetch fails.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.civil_respondent_id]);

  return code;
}
